<template>
  <div
    class="tab-content local-file-uploader-modal__content-wrapper"
    :class="{'is-uploading-converting': !staticState, 'is-tab-upload-image': isTabUploadImage}"
    @click="onUploaderClicked"
    tabindex="0"
  >
    <div class="local-file-uploader-modal__content">
      <div v-if="staticState" class="local-file-uploader-modal__content-text">
        <div v-if="isFileNameVisible" class="local-file-uploader__file-name">
          <p class="text-sm underline hover:no-underline flex justify-center items-center h-full">
            {{ fileName }}
          </p>
        </div>

        <div v-if="!isFileNameVisible">
          <aha-icon
            :name="icon.name"
            :width="icon.width"
            :height="icon.height"
            :viewBox="icon.viewBox"
            maintainColor
            class="tab-content__icon"
          />
          <div class="pt-4 pb-2 text-base text-gray-100">
            <span class="local-file-uploader__text-drag-drop">
              {{ $i18nPresenter.t("Click or drag file to this area to upload") }}
            </span>
          </div>
          <p v-if="instructionText" class="text-sm text-gray-70" v-html="instructionText" />
          <div
            class="pt-4 text-red-100 error-text text-sm"
            v-if="uploadError"
          />
        </div>
      </div>

      <div class="text-center" v-if="uploadingState || convertingState">
        <aha-icon
          :name="icon.name"
          :width="icon.width"
          :height="icon.height"
          :viewBox="icon.viewBox"
          maintainColor
          class="tab-content__icon"
        />
        <p class="font-bold pt-4" v-if="uploadingState">
          {{ $i18nPresenter.t("Uploading...") }}
        </p>
        <p class="font-bold pt-4" v-if="convertingState">
          {{ $i18nPresenter.t("Converting...") }}
        </p>
        <div class="py-2">
          <aha-spinner
            variant="dark"
          />
        </div>
        <div class="pt-2 text-sm">
          {{ $i18nPresenter.t("It might take a while to finish.") }}
        </div>
        <div class="pt-2 text-sm">
          <span class="pr-1">{{ $i18nPresenter.t("To continue uploading in the background, please") }}</span>
          <span
            class="text-primary-90 font-bold hover:text-primary-100 cursor-pointer"
            @click="hide()"
            @keydown.enter="hide()"
            @keydown.space="hide()"
            tabindex="0"
            role="button"
          >{{ $i18nPresenter.t("close this pop-up") }}<span>.</span>
          </span>
        </div>
      </div>

      <b-form-file
        v-model="file"
        :state="Boolean(file)"
        choose-label="Attachment2"
        :accept="fileFormat"
        id="upload-file"
        v-if="staticState"
        :drop-placeholder="dropPlaceholder"
        :file-name-formatter="null"
        @input="onInputFile"
        @mouseup.stop
      />
    </div>
  </div>
</template>

<script>
import EventBus from '@/eventBus'
import utils from '@/util'
import { mapGetters } from 'vuex'

import { CLICK_BROWSE_FILE } from '@/constant/eventActions/ImportEventActions.js'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'

// uploader
import BootstrapUploader from '@/utils/uploader/BootstrapUploader'
import AhaSlidesUploader from '@/utils/uploader/AhaSlidesUploader'

export default {
  props: {
    staticState: {
      type: Boolean,
      default: false,
    },
    uploadingState: {
      type: Boolean,
      default: false,
    },
    convertingState: {
      type: Boolean,
      default: false,
    },
    uploadError: {
      type: String,
      default: null,
    },
    featureName: {
      type: String,
      default: null,
    },
    needGenerateQuiz: {
      type: Boolean,
      default: false,
    },
    instructionText: {
      type: String,
      default: null,
    },
    customPropsForTracking: {
      type: Object,
      default: () => ({}),
    },
    fileFormat: {
      type: String,
      default: '.ppt, .pptx, .pdf',
    },
    icon: {
      type: Object,
      default: () => ({
        name: 'upload-simple',
        width: '32',
        height: '32',
        viewBox: '0 0 32 32',
      }),
    },
    dropPlaceholder: {
      type: String,
      default: 'drop image',
    },

    // UPLOAD IMAGE
    isTabUploadImage: {
      type: Boolean,
      default: false,
    },
    signedCFUrlExpiryDate: {
      type: Number,
      default: null,
    },
    featureToUploadImage: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      file: null,
      isUploadFileCalled: false,
    }
  },

  computed: {
    ...mapGetters('aiChatbotImportPptxPdf', ['aiChatbotUploadedFile']),

    fileName() {
      return this.aiChatbotUploadedFile?.file_metadata?.original_filename
    },

    isFileNameVisible() {
      return this.fileName && this.staticState
    },
  },
  mounted() {
    EventBus.$on('LocalFileUploader: uploadFile', (payload) => {
      this.uploadFile(payload)
    })
  },
  destroyed() {
    EventBus.$off('LocalFileUploader: uploadFile')
  },
  methods: {

    onInputFile(file) {
      this.file = file
      // Upload the file with the selected action
      this.uploadFile([this.file])
      // Emit event to parent to show import options modal
      this.$emit('show-import-options', file.name)
    },

    async uploadFile(files) {
      if (this.isUploadFileCalled) return // prevent doc-to-slide multiple calls
      this.isUploadFileCalled = true
      const bootstrapUploader = new BootstrapUploader(
        // local variables - GENERATE FROM DOC
        this.needGenerateQuiz,

        // local variables - IMPORT PPT PDF
        this.customPropsForTracking,

        // local variables - IMPORT SPREADSHEET
        this.$route.name,

        // local variables - UPLOAD IMAGE
        this.signedCFUrlExpiryDate,
        this.featureToUploadImage,
      )
      const ahaSlidesUploader = new AhaSlidesUploader(bootstrapUploader, this.featureName)
      ahaSlidesUploader.upload(files)
      await utils.sleep(1000)
      this.isUploadFileCalled = false
    },

    onUploaderClicked() {
      const eventAction = CLICK_BROWSE_FILE
      const eventCategory = EVENT_CATEGORIES_BY_ROUTE_NAME[this.$router?.currentRoute?.name]
      const trackData = {
        eventAction,
        eventCategory,
        source: this.featureName,
      }
      utils.trackGA4AndMixpanel(eventAction, trackData)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.local-file-uploader-modal__content-wrapper {
  @apply border-2 border-dashed hover:border-purple-30 p-4 border-gray-50;
  @apply bg-gray-20 rounded;
  @apply relative flex items-center justify-center text-xl cursor-pointer;
  height: $local-file-uploader-content-height;
  &.is-uploading-converting {
    @apply py-4;
  }
  .tab-content__browse-text {
    @apply text-primary-90 hover:text-primary-100 cursor-pointer relative;
    z-index: 2;
  }
  .local-file-uploader-modal__content {
    @apply text-center;
  }
  margin: 12px 0px;
  &:focus {
    outline: none;
    box-shadow: 0 0 0 4px rgba(211, 180, 255, 0.30);
  }
}
.custom-file {
  @apply absolute inset-0 overflow-hidden opacity-0 cursor-pointer z-1;
  & ::v-deep .custom-file-input {
    @apply w-full h-full;
  }
}
.local-file-uploader__text-drag-drop {
  @apply pb-1;
}
.local-file-uploader__file-name {
  height: $import-modal-input-content-height;
}
</style>
